import React, { useState, useRef, useEffect } from "react";
import {
  Box, Button, Typography, Paper, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, TablePagination, IconButton, Snackbar
} from "@mui/material";
import { Add, Edit, Delete, Undo } from "@mui/icons-material";
import OrderQuotationApp from "./OrderQuotationApp"; // 👈 Your Order Add/Edit Component
import { axiosInstances } from "./apiConfig";

export default function Order() {
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [total, setTotal] = useState(0);

  const deleteTimers = useRef({});
  const [pendingDeletes, setPendingDeletes] = useState({});
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // 🔹 Load orders from backend
  const loadOrders = async () => {
    try {
      const res = await axiosInstances.orderApi.get(`/?page=${page}&size=${rowsPerPage}`);
      const result = res.data.data || {};
      setOrders(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error("Error fetching orders", err);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 🔹 Open Add/Edit Order Form
  const openOrderForm = (order = null) => {
    setSelectedOrder(order);
    setShowOrderForm(true);
  };

  // 🔹 Delete order (with undo option)
  const handleDelete = (id) => {
    setPendingDeletes((prev) => ({ ...prev, [id]: true }));

    deleteTimers.current[id] = setTimeout(async () => {
      try {
        await axiosInstances.orderApi.post(`/delete`, { id });
        setSnackbar({ open: true, message: "Order deleted successfully!", severity: "success" });
        loadOrders();
      } catch (err) {
        console.error("Delete failed", err);
        setSnackbar({ open: true, message: "Something went wrong!", severity: "error" });
      } finally {
        setPendingDeletes((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        deleteTimers.current[id] = null;
      }
    }, 30000);
  };

  const handleUndo = (id) => {
    if (deleteTimers.current[id]) {
      clearTimeout(deleteTimers.current[id]);
      deleteTimers.current[id] = null;
    }

    setPendingDeletes((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  // 🔹 Render Order Form if adding or editing
  if (showOrderForm) {
    return (
      <OrderQuotationApp
        orderId={selectedOrder?.id}
        orderData={selectedOrder}
        onCancel={() => {
          setShowOrderForm(false);
          setSelectedOrder(null);
          loadOrders();
        }}
      />
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Order Management</Typography>

      <Button
        variant="contained"
        color="primary"
        startIcon={<Add />}
        onClick={() => openOrderForm()}
      >
        Add Order
      </Button>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>Order ID</TableCell>
              <TableCell>Order No</TableCell>
              <TableCell>Customer ID</TableCell>
              <TableCell>Offer ID</TableCell>
              <TableCell>Order Date</TableCell>
              <TableCell>SubTotal</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Tax(%)</TableCell>
              <TableCell>Net Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {orders.map((order, index) => (
              <TableRow key={order.id}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{order.id}</TableCell>
                <TableCell>{order.orderNo}</TableCell>
                <TableCell>{order.customerId}</TableCell>
                <TableCell>{order.offerId || "—"}</TableCell>
                <TableCell>{order.orderDate}</TableCell>
                <TableCell>{order.subTotal?.toFixed(2)}</TableCell>
                <TableCell>{(order.discount?.toFixed(2) || 0)}</TableCell>
                <TableCell>
                  igst: {order.igst * 100 || "0"} <br />
                  cgst: {order.cgst || "0"} <br />
                  sgst: {order.sgst || "0"}
                </TableCell>
                <TableCell>{order.grandTotal?.toFixed(2)}</TableCell>
                <TableCell>{order.ord_sts}</TableCell>

                <TableCell>
                  <IconButton color="primary" onClick={() => openOrderForm(order)}>
                    <Edit />
                  </IconButton>

                  {pendingDeletes[order.id] ? (
                    <IconButton color="warning" onClick={() => handleUndo(order.id)}>
                      <Undo />
                    </IconButton>
                  ) : (
                    <IconButton color="error" onClick={() => handleDelete(order.id)}>
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelDisplayedRows={({ page }) =>
            `Page ${page + 1} of ${Math.ceil(total / rowsPerPage)}`
          }
        />
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}
